import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';

const QRCodeList = () => {
  const [qrCodes, setQRCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteLoading, setDeleteLoading] = useState(null);

  useEffect(() => {
    fetchQRCodes();
  }, []);

  const fetchQRCodes = async () => {
    try {
      const response = await axios.get('/api/qr/list');
      setQRCodes(response.data.qrCodes || []);
    } catch (error) {
      console.error('Failed to fetch QR codes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this QR code?')) return;
    
    setDeleteLoading(id);
    try {
      await axios.delete(`/api/qr/${id}`);
      setQRCodes(qrCodes.filter(qr => qr.id !== id));
    } catch (error) {
      alert('Failed to delete QR code');
    } finally {
      setDeleteLoading(null);
    }
  };

  if (loading) {
    return <div className="loading">Loading your QR codes...</div>;
  }

  return (
    <div className="container">
      <div className="dashboard-header">
        <h1 style={{ 
          background: 'var(--gradient-primary)', 
          WebkitBackgroundClip: 'text', 
          WebkitTextFillColor: 'transparent' 
        }}>
          Your QR Codes
        </h1>
        <Link to="/qr-codes/create" className="btn btn-primary">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19"/>
            <line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          Create New
        </Link>
      </div>

      {qrCodes.length > 0 ? (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', 
          gap: '24px' 
        }}>
          {qrCodes.map((qr, index) => (
            <div 
              key={qr.id} 
              className="card" 
              style={{ 
                animationDelay: `${index * 0.1}s`,
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <div style={{ display: 'flex', gap: '20px', marginBottom: '16px' }}>
                <div style={{ 
                  background: 'white', 
                  padding: '12px', 
                  borderRadius: 'var(--radius-md)',
                  flexShrink: 0
                }}>
                  <QRCodeSVG value={qr.shortUrl} size={80} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h3 style={{ 
                    fontSize: '18px', 
                    marginBottom: '4px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {qr.title || 'Untitled'}
                  </h3>
                  {qr.description && (
                    <p style={{ 
                      color: 'var(--text-muted)', 
                      fontSize: '13px',
                      marginBottom: '8px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical'
                    }}>
                      {qr.description}
                    </p>
                  )}
                  <code style={{ fontSize: '12px' }}>{qr.short_code}</code>
                </div>
              </div>
              
              <div style={{ 
                display: 'flex', 
                gap: '16px', 
                padding: '12px 0',
                borderTop: '1px solid var(--border-color)',
                borderBottom: '1px solid var(--border-color)',
                marginBottom: '16px'
              }}>
                <div style={{ textAlign: 'center', flex: 1 }}>
                  <div style={{ 
                    fontSize: '24px', 
                    fontWeight: '700',
                    background: 'var(--gradient-primary)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}>
                    {qr.totalClicks || 0}
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Total Clicks
                  </div>
                </div>
                <div style={{ textAlign: 'center', flex: 1 }}>
                  <div style={{ 
                    fontSize: '24px', 
                    fontWeight: '700',
                    color: 'var(--accent-secondary)'
                  }}>
                    {qr.todayClicks || 0}
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Today
                  </div>
                </div>
              </div>
              
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <Link
                  to={`/qr-codes/view/${qr.id}`}
                  className="btn btn-primary"
                  style={{ flex: 1, padding: '10px', fontSize: '13px' }}
                >
                  View
                </Link>
                <Link
                  to={`/qr-codes/edit/${qr.id}`}
                  className="btn btn-secondary"
                  style={{ flex: 1, padding: '10px', fontSize: '13px' }}
                >
                  Edit
                </Link>
                <Link
                  to={`/analytics/${qr.id}`}
                  className="btn btn-ghost"
                  style={{ padding: '10px', fontSize: '13px' }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="20" x2="18" y2="10"/>
                    <line x1="12" y1="20" x2="12" y2="4"/>
                    <line x1="6" y1="20" x2="6" y2="14"/>
                  </svg>
                </Link>
                <button
                  onClick={() => handleDelete(qr.id)}
                  disabled={deleteLoading === qr.id}
                  className="btn btn-danger"
                  style={{ padding: '10px', fontSize: '13px' }}
                >
                  {deleteLoading === qr.id ? '...' : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="3 6 5 6 21 6"/>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="card">
          <div className="empty-state">
            <div className="empty-state-icon">
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7"/>
                <rect x="14" y="3" width="7" height="7"/>
                <rect x="14" y="14" width="7" height="7"/>
                <rect x="3" y="14" width="7" height="7"/>
              </svg>
            </div>
            <h3>No QR codes yet</h3>
            <p>Create your first QR code to start tracking clicks and generating revenue.</p>
            <Link to="/qr-codes/create" className="btn btn-primary">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"/>
                <line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
              Create Your First QR Code
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default QRCodeList;
